"use client";

import * as Dialog from "@radix-ui/react-dialog";
import { useToast } from "@/hooks/use-toast";
import {
  Search,
  Users,
  Linkedin,
  Megaphone,
  Newspaper,
  X,
  Pencil
} from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useTranslation } from "react-i18next";

import clsx from "clsx";

// Note that the value is what will be stored in the database.
const options = [
  {
    value: "Online search",
    icon: Search,
    translationKey: "common:onboarding_modal.search"
  },
  {
    value: "Friend",
    icon: Users,
    translationKey: "common:onboarding_modal.friend"
  },
  { value: "LinkedIn", icon: Linkedin },
  {
    value: "Advertisement",
    icon: Megaphone,
    translationKey: "common:onboarding_modal.ads"
  },
  {
    value: "News Article",
    icon: Newspaper,
    translationKey: "common:onboarding_modal.news_article"
  },
  {
    value: "Other",
    icon: Pencil,
    translationKey: "common:onboarding_modal.other"
  }
];

type OnBoardingMetadata = {
  seen_sign_up_onboarding: boolean;
  how_did_you_hear_about_us: string | null;
};
export default function OnboardingModal() {
  const [selected, setSelected] = useState<string | null>(null);
  const [onboardingData, setOnboardingData] =
    useState<OnBoardingMetadata | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { t: translation } = useTranslation(["auth", "common"]);
  const [userId, setUserId] = useState<string | null>(null);
  const [customText, setCustomText] = useState("");
  const isOther = selected === "Other";

  const { toast } = useToast();
  useEffect(() => {
    const fetchOnboardingData = async () => {
      const { data: userData, error: userError } =
        await supabase.auth.getUser();
      const userId = userData.user?.id;
      if (!userId || userError) {
        setIsLoading(false);
        return;
      }
      setUserId(userId);
      const { data, error } = await supabase
        .from("talent_profiles")
        .select("metadata")
        .eq("id", userId)
        .single();

      if (error) {
        setIsLoading(false);
        return;
      }

      setOnboardingData(data.metadata);
      setIsLoading(false);
    };

    fetchOnboardingData();
  }, []);

  const updateProfileMetadata = async (metadata: OnBoardingMetadata) => {
    const { error } = await supabase
      .from("talent_profiles")
      .update({ metadata })
      .eq("id", userId);

    if (error) {
      toast({
        title: translation("common:errors.error"),
        description: translation("common:errors.try_again_later"),
        variant: "destructive"
      });
      return;
    }

    setOnboardingData(metadata);
  };
  const handleHowDidYouHearAboutUs = async (howDidYouHearAboutUs: string) => {
    if (!userId) return;

    const updatedData = {
      ...onboardingData,
      seen_sign_up_onboarding: true,
      how_did_you_hear_about_us: howDidYouHearAboutUs
    };

    updateProfileMetadata(updatedData);
  };

  const handleCloseDialog = async () => {
    if (!userId) return;

    const updatedData = {
      ...onboardingData,
      seen_sign_up_onboarding: true,
      how_did_you_hear_about_us: null
    };

    updateProfileMetadata(updatedData);
  };

  if (isLoading) return null;

  if (!onboardingData || onboardingData.seen_sign_up_onboarding) {
    return null;
  }

  return (
    <Dialog.Root open>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />
        <Dialog.Content
          onInteractOutside={(e) => e.preventDefault()}
          className={clsx(
            "fixed z-50 bg-white shadow-xl p-6 md:rounded-2xl",
            "w-full h-full md:max-w-md md:h-auto",
            "top-0 left-0 md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2",
            "flex flex-col"
          )}
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">
              {translation("common:onboarding_modal.where_did_you_hear_of_us")}
            </h2>
            <Dialog.Close asChild>
              <button
                className="text-gray-500 hover:text-gray-700 transition md:absolute md:right-4 md:top-4"
                aria-label="Close"
                onClick={() => handleCloseDialog()}
              >
                <X className="w-5 h-5" />
              </button>
            </Dialog.Close>
          </div>

          <div className="flex flex-col gap-3">
            {options.map(({ value, translationKey, icon: Icon }) => (
              <button
                key={value}
                className={clsx(
                  "w-full flex items-center gap-3 px-4 py-3 rounded-lg border text-left",
                  selected === value
                    ? "bg-blue-100 border-blue-500"
                    : "hover:bg-gray-100"
                )}
                onClick={() => setSelected(value)}
              >
                <Icon className="w-5 h-5 text-gray-600" />
                <span className="text-sm font-medium">
                  {translationKey ? translation(translationKey) : value}
                </span>
              </button>
            ))}
            {isOther && (
              <textarea
                className="mt-2 w-full border rounded-lg p-3 text-sm resize-none min-h-[100px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Tell us how you heard about us..."
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
              />
            )}
          </div>

          <button
            className={clsx(
              "mt-auto md:mt-6 w-full bg-blue-600 text-white py-2 rounded-lg text-sm font-semibold",
              !selected && "opacity-50 cursor-not-allowed"
            )}
            disabled={!selected}
            onClick={() =>
              handleHowDidYouHearAboutUs(
                selected === "Other" ? customText : selected!
              )
            }
          >
            {translation("common:onboarding_modal.continue")}
          </button>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
